import { useState, useEffect, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Printer } from 'lucide-react'
import { service } from '../lib/api/service'
import type { Project, Surcharge } from '../lib/api/types'
import { formatPercent } from '../utils/calculations'

export default function ProjectSettingsPrint() {
  const navigate = useNavigate()
  const [project, setProject]       = useState<Project | null>(null)
  const [surcharges, setSurcharges] = useState<Surcharge[]>([])
  const [loading, setLoading]       = useState(true)

  useEffect(() => {
    Promise.all([service.getProject(), service.getSurcharges()])
      .then(([p, s]) => { setProject(p); setSurcharges(s) })
      .finally(() => setLoading(false))
  }, [])

  const doPrint = () => {
    document.body.dataset.printing = '1'
    window.print()
    delete document.body.dataset.printing
  }

  if (loading) return <div className="loading"><div className="spinner" /></div>

  const p: Partial<Project> = project ?? {}
  const today = new Date().toISOString().slice(0, 10)
  const activeSC = surcharges.filter(s => s.procent > 0 || s.typ)

  const Row = ({ label, value }: { label: string; value?: string | number | null }) => value ? (
    <tr>
      <td style={{ width: '40%', fontSize: '8.5pt', color: '#666', paddingRight: 8, verticalAlign: 'top', paddingBottom: '4pt' }}>{label}</td>
      <td style={{ fontSize: '10pt', paddingBottom: '4pt' }}>{value}</td>
    </tr>
  ) : null

  return (
    <div className="print-overlay">
      <div className="print-toolbar no-print">
        <button className="icon-btn" onClick={() => navigate('/projekt')}>
          <ArrowLeft size={14} /> Tillbaka
        </button>
        <span style={{ fontSize: 13, color: 'var(--ink-dim)' }}>Projektdatablad</span>
        <button className="btn-primary" style={{ marginLeft: 'auto' }} onClick={doPrint}>
          <Printer size={14} /> Skriv ut
        </button>
      </div>

      <div className="paper">
        <div className="paper-letterhead">
          <div>
            <div className="paper-sender-name">TIBRAM BYGG AB</div>
            <div className="paper-sender-addr">{p.adress ?? ''}</div>
          </div>
          <div>
            <div className="paper-doc-type">Projektdatablad</div>
            <div className="paper-doc-ref">{p.projektnr ?? '–'}</div>
          </div>
        </div>

        <div style={{ fontSize: '9pt', color: '#666', margin: '6pt 0 14pt' }}>
          Utskriven: {today}
        </div>

        {/* Grunddata */}
        <Section title="Grunddata">
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              <Row label="Projektnummer"  value={p.projektnr} />
              <Row label="Projektnamn"    value={p.projektNamn} />
              <Row label="Ort"            value={p.ort} />
              <Row label="Adress"         value={p.adress} />
              <Row label="Status"         value={p.projektStatus} />
              <Row label="Startdatum"     value={p.startdatum} />
              <Row label="Slutdatum"      value={p.slutdatum} />
              <Row label="Beskrivning"    value={p.beskrivning} />
            </tbody>
          </table>
        </Section>

        {/* Beställare */}
        <Section title="Beställare">
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              <Row label="Företag"        value={p.bestellare} />
              <Row label="Kontaktperson"  value={p.bestRef} />
              <Row label="Roll"           value={p.bestKontakt} />
              <Row label="E-post"         value={p.bestEmail} />
              <Row label="Telefon"        value={p.bestTelefon} />
            </tbody>
          </table>
        </Section>

        {/* Entreprenör */}
        <Section title="Entreprenör">
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              <Row label="Företag"              value={p.entreforetag} />
              <Row label="Platschef"            value={p.platschef} />
              <Row label="Arbetsledare"         value={p.arbetsledare} />
              <Row label="Org.nr"               value={p.orgNr} />
              <Row label="Tel. platskontor"     value={p.telPlatskontor} />
            </tbody>
          </table>
        </Section>

        {/* Ekonomi */}
        <Section title="Ekonomi & avtal">
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              <Row label="Kontraktssumma"       value={p.kontraktssumma != null ? p.kontraktssumma.toLocaleString('sv-SE') + ' kr' : null} />
              <Row label="Betalningsvillkor"    value={p.betalningsvillkor} />
              <Row label="Avtalsform"           value={p.avtalsform} />
              <Row label="Räntesats dröjsmål"   value={p.rantesats} />
              <Row label="Garantitid"           value={p.garantitid} />
            </tbody>
          </table>
        </Section>

        {/* Påslagstabell */}
        {activeSC.length > 0 && (
          <Section title="Påslagstabell">
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '.5pt solid #ccc' }}>
                  <th style={{ width: 30, textAlign: 'left', fontSize: '8pt', fontWeight: 700, padding: '3pt 4pt', color: '#555' }}>Kod</th>
                  <th style={{ textAlign: 'left', fontSize: '8pt', fontWeight: 700, padding: '3pt 4pt', color: '#555' }}>Benämning</th>
                  <th style={{ width: 60, textAlign: 'right', fontSize: '8pt', fontWeight: 700, padding: '3pt 4pt', color: '#555' }}>Procent</th>
                </tr>
              </thead>
              <tbody>
                {surcharges.map(s => (
                  <tr key={s.sortOrder} style={{ borderBottom: '.25pt solid #eee' }}>
                    <td style={{ fontSize: '9pt', padding: '2pt 4pt', fontFamily: 'monospace', color: '#666' }}>{s.sortOrder}</td>
                    <td style={{ fontSize: '9.5pt', padding: '2pt 4pt' }}>{s.typ || '–'}</td>
                    <td style={{ fontSize: '9.5pt', padding: '2pt 4pt', textAlign: 'right', fontFamily: 'monospace' }}>{s.procent ? formatPercent(s.procent) : '–'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>
        )}

        <div className="paper-footer">
          {p.projektnr ?? 'Projektdatablad'} &nbsp;—&nbsp; Utskriven {today}
        </div>
      </div>
    </div>
  )
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div style={{ marginBottom: '14pt' }}>
      <div style={{ fontSize: '8.5pt', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: '#555', borderBottom: '.5pt solid #ccc', paddingBottom: '3pt', marginBottom: '6pt' }}>
        {title}
      </div>
      {children}
    </div>
  )
}
